import { FC, useState, useMemo, useContext } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { Button, App, Dropdown, Tooltip } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { DictContext } from "@/layout/context/DictContext";
import { getFileUrl } from "@/apis/file";
import zioncom from 'images/system/zioncom.png';
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { OrderV2, OrderItemV2 } from "@/types/inventoryV2";
import { zhVnOpTypes2 } from "@/pages/Warehouse/utils";
import truncateString from "@/utils/stringOver";
import { getLocationIdx } from "@/utils/wLocation";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import { upload } from "@/utils/file/upload";
import { docAdd } from "@/apis/doc";
import { DocTypeEnum } from "@/types/doc";
import { orderPrint } from "@/apis/inventoryv2";
import { computedPrint, initPrintSetting } from "@/layout/HomeLayout/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },

  // 标题部分
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center' as any
  },
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black",
    marginTop: 8
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格样式
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as any,
    letterSpacing: 0,
    color: '#000'
  },

  // 条码单元格
  barcodeCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 1,
    fontSize: 5,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#000'
  },

  // 表头单元格
  tableHeaderCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center' as any
  },
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 24,
    left: 30,
    right: 30,
    textAlign: 'center' as any,
    fontSize: 6,
    color: '#000',
    letterSpacing: -1,
  },

  printInfo: {
    position: "absolute",
    bottom: 24,
    right: 30,
    fontSize: 6,
    color: "#000",
    textAlign: "right",
  },
  printCount: { fontSize: 8, position: "absolute", top: 8, right: 0, textAlign: "right" },
  printCountNormal: { fontSize: 8, position: "absolute", bottom: -10, right: 0, textAlign: "right" }
});

// 扩展类型以包含条码URL
type CodeUrlItem = OrderItemV2 & {
  codeUrl?: string;
  lotCodeUrl?: string;  // 批次号条码
}

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

interface PrintReturnProps {
  order: OrderV2;
  onStart?: () => void;
  onEnd?: () => void;
  onPrinted?: (printCount: number) => void; // 打印完成回调
  disabled?: boolean;
}

const PrintReturn: FC<PrintReturnProps> = ({ order, onStart, onEnd, onPrinted, disabled = false }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);
  const { printSetting } = useContext(DictContext);
  const curUser = useSelector((state: RootState) => state.userReducer.curUser)

  const { width: printWitdh, height: printHeight } = computedPrint();
  const { width: settingWitdh, height: settingHeight } = initPrintSetting();

  // 获取公司logo
  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting]);

  const printCountStr = useMemo(() => {
    return (order?.printCount || 0) + 1 + '';
  }, [order]);

  const printerStr = useMemo(() => {
    let time = dateFormat(dayjs(), true);
    if (!!curUser.name1 && !!curUser.name2) return `${curUser.name1}(${curUser.name2}) ${time}`
    else return `${curUser.name1 || curUser.name2 || curUser.mobile} ${time}`
  }, [curUser, order]);

  // 生成条码
  const handleCode = (code: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!code || code === '-') {
        resolve('');
        return;
      }
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, code, {
        format: "CODE128",
        width: 10,
        height: 20,
        displayValue: false,
        margin: 0,
        marginBottom: 6
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject("条码生成失败");
        }
      }, 'image/png');
    });
  };

  // 获取opType2文本
  const getOpType2Text = () => {
    if (!order.opType2) return '';
    return zhVnOpTypes2.find(op => op.value === order.opType2)?.label || '';
  };

  // 生成PDF文档
  const MyDocument = (codeItems: CodeUrlItem[], orderBarcode: string, orderNumber: string, opType2Text: string) => {
    let seq = 0;
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={[styles.page, { padding: 28 }]}>
          {/* 标题栏 - 只在第一页显示 */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>{name ?? '吉翁科技（深圳）有限公司'} - - - ({`${t('warehouse.returnStock', { lng: "zh" })}/${t('warehouse.returnStock', { lng: "vn" })}`})</Text>
            <Text style={{ fontSize: 10, textAlign: "center" }}>{`${t('warehouse.returnStock', { lng: "zh" })}/${t('warehouse.returnStock', { lng: "vn" })}`}</Text>
            {orderBarcode && (
              <View style={styles.titleBarcode}>
                <Image src={orderBarcode} style={{ width: 100, height: 28 }} />
                <Text style={styles.barcodeText}>{orderNumber || ''}</Text>
              </View>
            )}
            <Text style={styles.printCountNormal}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
          </View>

          {/* 表格 */}
          <View style={{ ...styles.table, borderLeftWidth: 0 }}>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outNo', { lng: "zh" })}(${t('warehouse.outNo', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '19%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '11%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.innerNo', { lng: "zh" })}(${t('warehouse.innerNo', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '40%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outData', { lng: "zh" })}(${t('warehouse.outData', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}> </View>
            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outWare', { lng: "zh" })}(${t('warehouse.outWare', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '19%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '11%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.llorg', { lng: "zh" })}(${t('warehouse.llorg', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '40%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.documentType', { lng: "zh" })}(${t('warehouse.documentType', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{opType2Text}</Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            {/* 表头 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]} fixed>
              <View style={[styles.tableHeaderCol, { width: '5%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.location', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.location', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.deliveryBatchNo', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.deliveryBatchNo', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '20%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '25%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.brand1', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.brand1', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.quantity', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.quantity', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "vn" }))}</Text>
              </View>
            </View>

            {/* 表体 */}
            {codeItems.map((item: CodeUrlItem, index: number) => {
              return (
                <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={index} wrap={false}>
                  <View style={[styles.tableCol, { width: '5%' }]}>
                    <Text style={styles.fontStyle}>{++seq}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '15%' }]}>
                    {item.locations && item.locations.length > 0 ? (
                      item.locations.map((loc, locIndex) => (
                        <Text key={locIndex} style={[styles.fontStyle, { fontSize: 6, letterSpacing: -1 }]}>
                          {getBlankWord(getLocationIdx(loc.location) || '-', true)}
                        </Text>
                      ))
                    ) : (
                      <Text style={styles.fontStyle}>{getBlankWord('-')}</Text>
                    )}
                  </View>
                  {/* 送货批次号列 */}
                  <View style={[styles.barcodeCol, { width: '15%' }]}>
                    {item.lotCodeUrl && (
                      <Image src={item.lotCodeUrl} style={{ width: "90%", height: 28 }} />
                    )}
                    <Text style={[styles.fontStyle, { fontSize: item.lotCodeUrl ? 5 : 6, marginTop: item.lotCodeUrl ? -5 : 0, letterSpacing: -1, width: "90%" }]}>
                      {getBlankWord(item.lot || '-', true)}
                    </Text>
                  </View>
                  <View style={[styles.barcodeCol, { width: '20%' }]}>
                    {item.codeUrl && (
                      <Image src={item.codeUrl} style={{ width: "90%", height: 28 }} />
                    )}
                    <Text style={[styles.fontStyle, { fontSize: item.codeUrl ? 5 : 6, marginTop: item.codeUrl ? -5 : 0 }]}>
                      {getBlankWord(`${item?.product?.code ?? ''} / ${item?.product?.name ?? ''}`)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '25%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(truncateString(item.product?.model || '-', 38), true)}
                    </Text>
                  </View>
                  {/* 数量和单位合并到一个view，包含上下两部分 */}
                  <View style={[styles.tableCol, { width: '20%', padding: 0, flexDirection: 'column', justifyContent: "flex-start" }]}>
                    {/* 上部分：数量和单位两个单元格 */}
                    <View style={{ flexDirection: 'row', width: '100%', borderBottomWidth: 1, borderColor: 'black' }}>
                      <View style={{ width: '50.5%', borderRightWidth: 1, borderColor: 'black', padding: 2, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={[styles.fontStyle]}>{getBlankWord(String(item.count || '0'))}</Text>
                      </View>
                      <View style={{ width: '49.5%', padding: 2, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={[styles.fontStyle, { flexWrap: 'wrap' }]}>{getBlankWord(item.product?.uom || '-')}</Text>
                      </View>
                    </View>
                    {/* 下部分：备注 */}
                    <View style={{ width: '100%', padding: 2, minHeight: 20 }}>
                      <Text style={[styles.fontStyle, { textAlign: 'left', fontSize: 5 }]}>{t('purchase.remark', { lng: "zh" })}({t('purchase.remark', { lng: "vn" })}):</Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Logo图片 - 只在第一页显示 */}
          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />

          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.outper', { lng: "zh" })}(${t('warehouse.outper', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.wareper', { lng: "zh" })}(${t('warehouse.wareper', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.outCheckDate', { lng: "zh" })}(${t('warehouse.outCheckDate', { lng: "vn" })})`}</Text>
            </View>
          </View>

          {/* 页脚 */}
          <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
            `P ${pageNumber} / ${totalPages}`
          } fixed />
          <Text fixed style={styles.printInfo} >{printerStr}</Text>
        </Page>
      </Document >
    );
  };
  const MyVerticalDocument = (codeItems: CodeUrlItem[], orderNumber: string, opType2Text: string) => {
    return (
      <Document>
        <Page size={{ width: printWitdh, height: printHeight }} orientation="portrait" style={[styles.page, { paddingHorizontal: 4 }]} wrap>
          <View fixed render={({ pageNumber }) => {
            return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000" }} />
          }}>
          </View>
          <View style={styles.titleSection}>
            <Text style={styles.title}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
            <Text style={{ fontSize: 10, textAlign: "center" }}>
              {`${t('warehouse.returnStock', { lng: "zh" })}/${t('warehouse.returnStock', { lng: "vn" })}`}
              {'    '}
              {orderNumber ?? ''}
            </Text>
            <Text style={styles.printCount}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
          </View>
          {/* 表格 */}
          <View style={{ ...styles.table, flexShrink: 0, borderLeftWidth: 0, marginTop: 0 }}>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outNo', { lng: "zh" })}(${t('warehouse.outNo', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '19%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '11%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.innerNo', { lng: "zh" })}(${t('warehouse.innerNo', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '40%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outData', { lng: "zh" })}(${t('warehouse.outData', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}> </View>
            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.outWare', { lng: "zh" })}(${t('warehouse.outWare', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '19%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '11%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.llorg', { lng: "zh" })}(${t('warehouse.llorg', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '40%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(`${t('warehouse.documentType', { lng: "zh" })}(${t('warehouse.documentType', { lng: "vn" })})`)} </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{opType2Text}</Text>
              </View>
            </View>
            {/* 表头 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '5%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.location', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.location', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.deliveryBatchNo', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.deliveryBatchNo', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '20%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '25%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.brand1', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.brand1', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.quantity', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.quantity', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "vn" }))}</Text>
              </View>
            </View>

            {/* 表体 */}
            {codeItems.map((item: CodeUrlItem, index: number) => {
              return (
                <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={index} wrap={false}>
                  <View style={[styles.tableCol, { width: '5%' }]}>
                    <Text style={styles.fontStyle}>{index + 1}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '15%' }]}>
                    {item.locations && item.locations.length > 0 ? (
                      item.locations.map((loc, locIndex) => (
                        <Text key={locIndex} style={[styles.fontStyle, { fontSize: 6, letterSpacing: -1 }]}>
                          {getBlankWord(getLocationIdx(loc.location) || '-', true)}
                        </Text>
                      ))
                    ) : (
                      <Text style={styles.fontStyle}>{getBlankWord('-')}</Text>
                    )}
                  </View>
                  {/* 送货批次号列 */}
                  <View style={[styles.barcodeCol, { width: '15%' }]}>
                    <Text style={[styles.fontStyle, { letterSpacing: -1, width: "90%" }]}>
                      {getBlankWord(item.lot || '-', true)}
                    </Text>
                  </View>
                  <View style={[styles.barcodeCol, { width: '20%' }]}>
                    <Text style={[styles.fontStyle]}>
                      {getBlankWord(`${item?.product?.code ?? ''} / ${item?.product?.name ?? ''}`)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '25%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(item.product?.model || '-', true)}
                    </Text>
                  </View>
                  {/* 数量和单位合并到一个view，包含上下两部分 */}
                  <View style={[styles.tableCol, { width: '20%', padding: 0, flexDirection: 'column', justifyContent: "flex-start" }]}>
                    {/* 上部分：数量和单位两个单元格 */}
                    <View style={{ flexDirection: 'row', width: '100%', borderBottomWidth: 1, borderColor: 'black' }}>
                      <View style={{ width: '50.5%', borderRightWidth: 1, borderColor: 'black', padding: 2, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={[styles.fontStyle]}>{getBlankWord(String(item.count || '0'))}</Text>
                      </View>
                      <View style={{ width: '49.5%', padding: 2, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={[styles.fontStyle, { flexWrap: 'wrap' }]}>{getBlankWord(item.product?.uom || '-')}</Text>
                      </View>
                    </View>
                    {/* 下部分：备注 */}
                    <View style={{ width: '100%', padding: 2, minHeight: 20 }}>
                      <Text style={[styles.fontStyle, { textAlign: 'left', fontSize: 5 }]}>{t('purchase.remark', { lng: "zh" })}({t('purchase.remark', { lng: "vn" })}):</Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>

          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.outper', { lng: "zh" })}(${t('warehouse.outper', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.wareper', { lng: "zh" })}(${t('warehouse.wareper', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('warehouse.outCheckDate', { lng: "zh" })}(${t('warehouse.outCheckDate', { lng: "vn" })})`}</Text>
            </View>
          </View>
          <View style={{ marginTop: 30, flexDirection: "row", justifyContent: "flex-end" }}>
            <Text style={{ bottom: 16, right: 4, fontSize: 8 }} >{printerStr}</Text>
          </View>
        </Page>
      </Document>
    );
  };

  // 处理PDF打印
  const handlePDF = async (flag: boolean) => {
    if (!order?.items || order.items.length === 0) {
      return notification.error({
        placement: 'top',
        message: t('purchase.printErr'),
        description: t('bom.errTip')
      });
    }

    setLoading(true);
    if (onStart) onStart();

    try {
      // 生成订单号条码
      let orderBarcode = '';
      try {
        if (order.number) {
          orderBarcode = await handleCode(order.number);
        }
      } catch (error) {
        console.error('订单号条码生成失败:', error);
      }

      // 为每个产品生成条码
      let codeItems: CodeUrlItem[] = [];
      for (let i = 0; i < order.items.length; i++) {
        const item = order.items[i];
        let codeUrl = '';
        let lotCodeUrl = '';
        try {
          if (item.product?.code) {
            codeUrl = await handleCode(item.lot);
          }
        } catch (error) {
          console.error('条码生成失败:', item.product?.code, error);
        }
        // 生成批次号条码
        try {
          if (item.lot) {
            lotCodeUrl = await handleCode(item.lot);
          }
        } catch (error) {
          console.error('批次号条码生成失败:', item.lot, error);
        }
        codeItems.push({
          ...item,
          codeUrl,
          lotCodeUrl
        });
      }

      // 生成PDF文档
      const opType2Text = getOpType2Text();
      const pageElement = flag ? MyVerticalDocument(codeItems, order.number ?? "", opType2Text) : MyDocument(codeItems, orderBarcode, order.number, opType2Text);
      if (!pageElement) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        return;
      }

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();

      let name = `${order.number}-${printerStr}(${flag ? t('warehouse.verticalPrint') : t('warehouse.normalPrint')}).pdf`;
      let docId = order.docId;
      if (docId) {
        let file = await upload({ name, size: blob.size, file: blob });
        if (file) {
          let doc = await docAdd({ pId: docId, name, suffix: file.suffix, nodeType: DocTypeEnum.LEAF, fileId: file.id });
          await orderPrint(order.id, doc.id);
          // 打印完成后，更新打印次数
          onPrinted?.((order.printCount ?? 0) + 1);
        }
      }

      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: '打印失败' });
        } finally {
          setLoading(false);
          if (onEnd) onEnd();
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: '生成PDF失败' });
      setLoading(false);
      if (onEnd) onEnd();
    }
  };

  return (
    <Dropdown trigger={["click"]}
      menu={{
        items: [
          {
            key: 'NORM',
            label: t('warehouse.normalPrint'),
            onClick: () => handlePDF(false),

          },
          {
            key: 'WARE',
            label: <Tooltip title={t('layout.printTip', { width: settingWitdh, height: settingHeight })}>{t('warehouse.verticalPrint')}</Tooltip>,
            onClick: () => handlePDF(true),
          },
        ],
      }}
    >
      <Button type="primary" loading={loading}>{t('common.printInfo')}</Button>
    </Dropdown>
  );

};
export default PrintReturn;